/**
 * Generated by scripts/design-system/regenerate-design-system.cjs.
 * Source spec hash: e0df96bf27cecba3b0e19a269be5fad5bedc28b4bbf3d99c89800dc2f5ca720c
 */
import cn from 'classnames'
import React, { ComponentProps, ReactElement } from 'react'

import Button from '../Button/Button'
import style from './DropdownButton.style'

type DropdownButtonTriggerProps = {
  'aria-controls'?: string
  'aria-expanded'?: boolean
  'aria-haspopup'?: 'menu'
  'data-dropdown-button-trigger'?: string
  onClick?: React.MouseEventHandler<HTMLElement>
  onMouseEnter?: React.MouseEventHandler<HTMLElement>
  onMouseLeave?: React.MouseEventHandler<HTMLElement>
}

type DropdownButtonCloseOptions = {
  returnFocus?: boolean
}

type DropdownButtonOpenSource = 'click' | 'controlled' | 'hover'

const FOCUSABLE_MENU_ITEM_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), [role="menuitem"]'
const CLOSE_ON_SELECT_SELECTOR = '[role="menuitem"], a[href]'

export type DropdownButtonProps = Omit<ComponentProps<'div'>, 'children' | 'onToggle'> & {
  children?: React.ReactNode
  className?: string
  closeOnSelect?: boolean
  defaultOpen?: boolean
  iconLeft?: React.ReactNode
  iconRight?: React.ReactNode
  isOpen?: boolean
  menu: React.ReactNode
  onToggle?: (next: boolean) => void
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'
  trigger?: React.ReactNode
  triggerOn?: 'click' | 'hover'
  variant?: 'default' | 'secondary' | 'severe' | 'severeSecondary' | 'noShell' | 'inline' | 'inlineMuted' | 'inlineSevere'
}

function DropdownButton(props: DropdownButtonProps): ReactElement {
  const {
    children,
    className = '',
    closeOnSelect = true,
    defaultOpen = false,
    iconLeft,
    iconRight,
    isOpen,
    menu,
    onMouseEnter,
    onMouseLeave,
    onToggle,
    placement = 'bottom-start',
    trigger,
    triggerOn = 'click',
    variant = 'secondary',
    ...rest
  } = props
  const menuId = React.useId()
  const rootRef = React.useRef<HTMLDivElement>(null)
  const closeTimerRef = React.useRef<number | null>(null)
  const openSourceRef = React.useRef<DropdownButtonOpenSource>('controlled')
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen)
  const open = isOpen ?? uncontrolledOpen
  const openRef = React.useRef(open)

  React.useEffect(() => {
    openRef.current = open
  }, [open])

  const clearHoverCloseTimer = React.useCallback(() => {
    if (closeTimerRef.current === null) {
      return
    }

    window.clearTimeout(closeTimerRef.current)
    closeTimerRef.current = null
  }, [])

  const focusTrigger = React.useCallback(() => {
    window.setTimeout(() => {
      rootRef.current
        ?.querySelector<HTMLElement>('[data-dropdown-button-trigger]')
        ?.focus()
    }, 0)
  }, [])

  const focusFirstMenuItem = React.useCallback(() => {
    window.setTimeout(() => {
      rootRef.current
        ?.querySelector<HTMLElement>(`.menu ${FOCUSABLE_MENU_ITEM_SELECTOR}`)
        ?.focus()
    }, 0)
  }, [])

  const setOpenState = React.useCallback(
    (nextOpen: boolean, source: DropdownButtonOpenSource = 'controlled') => {
      if (nextOpen === openRef.current) {
        return
      }

      openRef.current = nextOpen

      if (nextOpen) {
        openSourceRef.current = source
      }

      if (isOpen === undefined) {
        setUncontrolledOpen(nextOpen)
      }

      onToggle?.(nextOpen)
    },
    [isOpen, onToggle],
  )

  const close = React.useCallback((options: DropdownButtonCloseOptions = {}) => {
    const { returnFocus = false } = options

    clearHoverCloseTimer()

    if (!openRef.current) {
      return
    }

    setOpenState(false)

    if (!returnFocus) {
      return
    }

    focusTrigger()
  }, [clearHoverCloseTimer, focusTrigger, setOpenState])

  const openFromHover = React.useCallback(() => {
    if (triggerOn !== 'hover') {
      return
    }

    clearHoverCloseTimer()
    setOpenState(true, 'hover')
  }, [clearHoverCloseTimer, setOpenState, triggerOn])

  const queueHoverClose = React.useCallback(() => {
    if (triggerOn !== 'hover') {
      return
    }

    clearHoverCloseTimer()
    closeTimerRef.current = window.setTimeout(close, 100)
  }, [clearHoverCloseTimer, close, triggerOn])

  React.useEffect(() => clearHoverCloseTimer, [clearHoverCloseTimer])

  React.useEffect(() => {
    if (!open || openSourceRef.current === 'hover') {
      return
    }

    focusFirstMenuItem()
  }, [focusFirstMenuItem, open])

  React.useEffect(() => {
    if (!open) {
      return undefined
    }

    const handleOutsidePointerDown = (event: MouseEvent) => {
      const target = event.target

      if (!(target instanceof Node) || rootRef.current?.contains(target)) {
        return
      }

      close()
    }

    document.addEventListener('mousedown', handleOutsidePointerDown)

    return () => {
      document.removeEventListener('mousedown', handleOutsidePointerDown)
    }
  }, [close, open])

  React.useEffect(() => {
    if (!open) {
      return undefined
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return
      }

      event.preventDefault()
      close({ returnFocus: true })
    }

    document.addEventListener('keydown', handleEscapeKey)

    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [close, open])

  const handleTriggerClick = React.useCallback(() => {
    clearHoverCloseTimer()
    setOpenState(!openRef.current, 'click')
  }, [clearHoverCloseTimer, setOpenState])

  const triggerProps = {
    'aria-controls': menuId,
    'aria-expanded': open,
    'aria-haspopup': 'menu' as const,
    'data-dropdown-button-trigger': '',
    onClick: handleTriggerClick,
    onMouseEnter: openFromHover,
    onMouseLeave: queueHoverClose,
  }

  const renderedTrigger = React.isValidElement<DropdownButtonTriggerProps>(trigger) ? (
    React.cloneElement(trigger, {
      ...triggerProps,
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        trigger.props.onClick?.(event)

        if (!event.defaultPrevented) {
          handleTriggerClick()
        }
      },
      onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
        trigger.props.onMouseEnter?.(event)
        openFromHover()
      },
      onMouseLeave: (event: React.MouseEvent<HTMLElement>) => {
        trigger.props.onMouseLeave?.(event)
        queueHoverClose()
      },
    })
  ) : (
    <Button
      aria-controls={menuId}
      aria-expanded={open}
      aria-haspopup='menu'
      data-dropdown-button-trigger=''
      iconLeft={iconLeft}
      iconRight={iconRight}
      onClick={handleTriggerClick}
      onMouseEnter={openFromHover}
      onMouseLeave={queueHoverClose}
      type='button'
      variant={variant}
    >
      {children}
    </Button>
  )

  const handleMenuClick = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!closeOnSelect) {
      return
    }

    const target = event.target

    if (!(target instanceof Element) || !target.closest(CLOSE_ON_SELECT_SELECTOR)) {
      return
    }

    close({ returnFocus: true })
  }, [close, closeOnSelect])

  const handleRootMouseEnter: React.MouseEventHandler<HTMLDivElement> = (event) => {
    onMouseEnter?.(event)
    openFromHover()
  }

  const handleRootMouseLeave: React.MouseEventHandler<HTMLDivElement> = (event) => {
    onMouseLeave?.(event)
    queueHoverClose()
  }

  return (
    <div
      {...rest}
      className={cn('DropdownButton', className, { open })}
      css={style}
      onMouseEnter={handleRootMouseEnter}
      onMouseLeave={handleRootMouseLeave}
      ref={rootRef}
      role='presentation'
    >
      {renderedTrigger}
      {open ? (
        <div
          className={cn('menu', placement)}
          id={menuId}
          onClick={handleMenuClick}
          onMouseEnter={openFromHover}
          onMouseLeave={queueHoverClose}
          role='presentation'
        >
          {menu}
        </div>
      ) : null}
    </div>
  )
}

export default DropdownButton
